import { useConfig as hook, BaseConfig } from '@eika-infrastruktur/config-provider';

import type { Context } from './generated-types';
import nbTranslations from './translations/nbTranslations-config';
import nnTranslations from './translations/nnTranslations-config';
import TranslationsConfigType from './translations/translationType';

export interface Link {
    target?: string;
    text?: string;
    url: string;
}

export type FeatureToggles = {
    enableEkbLoans: boolean;
    enableLoanArrears: boolean;
    enableRejectedPayments: boolean;
    enableStacktrace: boolean;
    enableCreditCardsFrontpage: boolean;
    enableRejectedPaymentItems: boolean;
    enableSecureCommunication: boolean;
    disableEfakturaNotifications: boolean;
    disableEfakturaHistory: boolean;
    disableAvtaleGiroChanges: boolean;
    enableRunningBalance: boolean;
    enableRunningBalanceForFuturePayments: boolean;
    enableTransactionDetailsReservedFields: boolean;
    enablePaymentLimits: boolean;
    showTransactionPdfLink: boolean;
    enableUpdateFutureEfaktura: boolean;
    enableBulkPayment: boolean;
    enableTransactionCategoryFilter: boolean;
    enableTransactionFilter: boolean;
    enableTransactionFreetextSearch: boolean;
    enableTransactionDownload: boolean;
    enableUpdatingMobilenumber: boolean;
    enableUpdatingEmail: boolean;
    enableGettingAccountWithdrawalsInPayment: boolean;
    enableGettingAccountWithdrawalsInAccountDetails: boolean;
    enableRestartPaymentItem: boolean;
    enableCreateCrossBorderReceiver: boolean;
    enableCloseAccount: boolean;
    enableGettingDocumentsForSigning: boolean;
    enableLoanRepaymentPlan: boolean;
    enableEditLoan: boolean;
    enablePayNow: boolean;
    enableKidValidation: boolean;
    enableAdditionalInfoTransactions: boolean;
    disableNextSalary: boolean;
    enablePushNotifications: boolean;
    enablePushRules: boolean;
    disablePushPopup: boolean;

    enableChangeAppIcon: boolean;
    disableCrossBorderPayments: boolean;
    enableCrossBorderPriority: boolean;
    enableDownpayment: boolean;
    enableAvdragsfrihet: boolean;
    enableLanguageSelection: boolean;
    enableAddAuthorizedCustomer: boolean;
    enableEFakturaAccount: boolean;
    enableShareEfaktura: boolean;
    enableOutbox: boolean;
    enablePurposeInList: boolean;
    enableAskForReview: boolean;
    enableChooseLoginAndSigningMethod: boolean;
    disableEfaktura: boolean;
    disableAvtalegiro: boolean;
    enableSillyTaskManagerEmptyState: boolean;
    enableStoppedCrossBorderPayments: boolean;
    disableCrossborderInfo: boolean;
    disableMyHouse: boolean;
    disableOwnText: boolean;
    disableOwnTransferText: boolean;
    enableLastLogin: boolean;
    enableAvtaleGiroProposals: boolean;
    enableCompanyInfo: boolean;
    enableUpdatingGeneralManager: boolean;
    enableCheckAvtalegiroKid: boolean;
    disableAccountCreation: boolean;
    useMobileForAdvisor: boolean;
    enableFakedLoanFuturePayment: boolean;
    enablePeriodicControl: boolean;
    enableLoanApplication: boolean;
    enableBlockRiskyDevicePaymentLimit: boolean;
    enableBecomeCustomerChild: boolean;
    enableAccountsDnd: boolean;
    enableLoanStakeholders: boolean;
    enableHideSigningRoomLinkInSigningRoom: boolean;
    enableCustomerDialogue: boolean;
    enableImmediatePayment: boolean;
    enableImmediatePaymentCreditorCheck: boolean;
    enableStepUp: boolean;
    enableStoppedAvtalegiroEdit: boolean;
    enableFetchAvtalegiros: boolean;
    showOldSystemDeletionAttentionMessage: boolean;
    enabledStoppedPaymentsFromPending: boolean;
    nudgePushOnEfaktura: boolean;
    showAvtalegiroDelay: boolean;
    enableWispSignal: boolean;
    enableKredittbankenTasks: boolean;
    showSystemErrorsInTaskManager: boolean;
    disableWaitingForPopupClients: boolean;
};

export type FeatureToggleRules = {
    messages: boolean;
    loans: boolean;
    overdueEfaktura: boolean;
    stoppedPayment: boolean;
    salary: boolean;
    account: boolean;
};

export interface Config extends BaseConfig {
    allAccountsLink: Link;
    editAccountsLink?: Link;
    createAccountsLink?: Link;
    accountDetailsLink?: Link;
    accountLink?: Link;
    efakturaHistoryLink?: Link;
    efakturaReservationsLink?: Link;
    efakturaNotificationsLink?: Link;
    efakturaArchiveLink?: Link;
    loanLink?: Link;
    editLoanLink?: Link;
    loanDetailsLink?: Link;
    loanRepaymentPlanLink?: Link;
    loginSettingsLink?: Link;
    paymentSettingsLink?: Link;
    myActivationsLink?: Link;
    paymentPageLink?: Link;
    stoppedPaymentsLink?: Link;
    paymentsForApprovalLink?: Link;
    selectSigningAndLoginMethodLink?: Link;
    myConsentsLink?: Link;
    priceListLink?: Link;
    privacyLink?: Link;
    profileLink?: Link;
    cibLink?: Link;
    aboutAppLink?: Link;
    thirdPartyDependenciesLink?: Link;
    avtalegiroLink?: Link;
    futurePaymentsLink?: Link;
    approvalListLink?: Link;
    recurringPaymentLink?: Link;
    smalanLink?: Link;
    salgspantlaanLink?: Link;
    nettpostLink?: Link;
    messagesLink?: Link;
    logoutLink: Link;
    contactPageLink?: Link;
    crossBorderPaymentLink?: Link;
    cancelCrossBorderPaymentLink?: Link;
    paymentLimitsLink?: Link;
    frontPageLink?: Link;
    futurePaymentsListLink: Link;
    ahvSigningLink?: Link;
    editSalaryInfoLink?: Link;
    editSalaryInfoStartPageLink?: Link;
    changeLanguagePageLink?: Link;
    avdragsfrihetLink?: Link;
    minBoligLink?: Link;
    contactInfo?: Link;
    notificationsSettingsLink?: Link;
    shareEfakturaLink?: Link;
    stepUpAuthLink?: Link;
    loanApplicationLink?: Link;
    getLoanLink?: Link;
    becomeCustomerChildLink?: Link;
    pushSettingsLink?: Link;
    fetchAvtalegiroLink?: Link;
    demoCurrencyCalculatorLink?: Link;
    birthday18Link?: Link;
    priceListInstantPaymentLink?: Link;
    kredittbankenKycLink?: Link;
    kredittbankenKycReturnLink?: Link;
    siteLang: 'nb' | 'nn';
    translations: {
        nb: TranslationsConfigType;
        nn: TranslationsConfigType;
    };
    appContext: Context;
    featureToggles: {
        rules: FeatureToggleRules;
    } & FeatureToggles;
    minimumAppVersion: string;
    appStoreUpdateLink: string;
    googlePlayUpdateLink: string;
    isNative?: boolean;
    frontpage?: boolean;
    minutesUntilLogOutWarning: number;
    noteMaxLength: number;
    noteMaxLengthSmall: number;
    blockedImmediateBanks: string;
    validCardKos: string;
}

export const defaultConfig: Config = {
    allAccountsLink: {
        url: '/accounts',
    },
    editAccountsLink: {
        url: '/accounts/edit',
    },
    logoutLink: {
        url: '/loggut',
    },
    paymentPageLink: {
        url: '/payment',
    },
    stoppedPaymentsLink: {
        url: '/stopped-payments',
    },
    futurePaymentsListLink: {
        url: '/futurepaymentslist',
    },
    ahvSigningLink: {
        url: '/esignering',
    },
    siteLang: 'nb' as const,
    translations: {
        nb: nbTranslations,
        nn: nnTranslations,
    },
    featureToggles: {
        enableEkbLoans: true,
        enableLoanArrears: true,
        enableStacktrace: false,
        enableCreditCardsFrontpage: true,
        enableRejectedPayments: true,
        enableRejectedPaymentItems: true,
        enableSecureCommunication: true,
        disableEfakturaNotifications: false,
        disableEfakturaHistory: false,
        disableAvtaleGiroChanges: false,
        enableRunningBalance: true,
        enableRunningBalanceForFuturePayments: true,
        enableTransactionDetailsReservedFields: true,
        enablePaymentLimits: true,
        showTransactionPdfLink: true,
        enableUpdateFutureEfaktura: true,
        enableBulkPayment: true,
        enableTransactionCategoryFilter: true,
        enableTransactionFilter: true,
        enableTransactionFreetextSearch: true,
        enableTransactionDownload: true,
        enableUpdatingMobilenumber: false,
        enableUpdatingEmail: true,
        enableGettingAccountWithdrawalsInPayment: true,
        enableGettingAccountWithdrawalsInAccountDetails: true,
        enableRestartPaymentItem: true,
        enableCreateCrossBorderReceiver: true,
        enableCloseAccount: true,
        enableGettingDocumentsForSigning: true,
        enableLoanRepaymentPlan: true,
        enableEditLoan: true,
        enablePayNow: true,
        enableKidValidation: true,
        enableAdditionalInfoTransactions: true,
        disableNextSalary: false,
        enablePushNotifications: false,
        enablePushRules: false,
        disablePushPopup: false,
        rules: {
            messages: false,
            loans: false,
            overdueEfaktura: false,
            stoppedPayment: false,
            salary: false,
            account: false,
        },
        enableChangeAppIcon: true,
        disableCrossBorderPayments: false,
        enableCrossBorderPriority: false,
        enableDownpayment: true,
        enableAvdragsfrihet: false,
        enableLanguageSelection: false,
        enableAddAuthorizedCustomer: true,
        enableEFakturaAccount: true,
        enableShareEfaktura: true,
        enableOutbox: true,
        enableAskForReview: true,
        enableChooseLoginAndSigningMethod: true,
        enablePurposeInList: true,
        disableEfaktura: false,
        disableAvtalegiro: false,
        enableSillyTaskManagerEmptyState: false,
        enableStoppedCrossBorderPayments: false,
        disableCrossborderInfo: false,
        disableMyHouse: false,
        disableOwnText: false,
        disableOwnTransferText: true,
        enableLastLogin: true,
        enableAvtaleGiroProposals: true,
        enableCompanyInfo: true,
        enableUpdatingGeneralManager: true,
        enableCheckAvtalegiroKid: true,
        disableAccountCreation: false,
        useMobileForAdvisor: false,
        enableFakedLoanFuturePayment: false,
        enablePeriodicControl: false,
        enableLoanApplication: false,
        enableBlockRiskyDevicePaymentLimit: true,
        enableBecomeCustomerChild: true,
        enableAccountsDnd: false,
        enableLoanStakeholders: true,
        enableHideSigningRoomLinkInSigningRoom: false,
        enableCustomerDialogue: false,
        enableImmediatePayment: true,
        enableImmediatePaymentCreditorCheck: true,
        enableStepUp: true,
        enableStoppedAvtalegiroEdit: true,
        enableFetchAvtalegiros: false,
        showOldSystemDeletionAttentionMessage: false,
        enabledStoppedPaymentsFromPending: false,
        nudgePushOnEfaktura: false,
        showAvtalegiroDelay: true,
        enableWispSignal: true,
        enableKredittbankenTasks: true,
        showSystemErrorsInTaskManager: false,
        disableWaitingForPopupClients: false,
    },
    minimumAppVersion: '1.24.0',
    appStoreUpdateLink: 'https://apps.apple.com/my/app/eika-mobilbank/id1533222919?ign-mpt=uo%3D2',
    googlePlayUpdateLink: 'https://play.google.com/store/apps/details?id=no.eika.mobilbank',
    minutesUntilLogOutWarning: 10,
    noteMaxLength: 40,
    noteMaxLengthSmall: 30,
    blockedImmediateBanks: '8301,9180,9602',
    appContext: 'PRIVATE',
    validCardKos:
        '100101,100102,100103,100104,100105,100107,100108,700101,110101,110102,110103,110104,110105,110106,110107,110108,110601,110602,110603,110302,110303,10101,10103,10104,10105,10106,440101',
};

export const demoConfig: Config = {
    ...defaultConfig,
    editAccountsLink: undefined,
    loanLink: {
        url: '/demo/loan-application',
    },
    paymentPageLink: {
        url: '/demo/payment',
    },
    accountLink: {
        url: '/demo/details',
    },
    demoCurrencyCalculatorLink: {
        url: '/demo/currency-calculator',
    },
};

function createHook<T>(): () => T {
    return () => hook<T>();
}

export const useConfig = createHook<typeof defaultConfig>();
